/**
 * Open-work handover (2026-10-15): when HQ removes or archives a staff member
 * it offers to move their open work — tasks, event assignments, owned events
 * and deals — to someone else first. Each app that holds that work exposes two
 * service endpoints, and HQ fans out to them:
 *
 *   GET  /api/service/open-work/:multipassUserId           → `OpenWorkPreview`
 *   POST /api/service/open-work/:multipassUserId/reassign  → `OpenWorkReassignResult`
 *        body `OpenWorkReassignRequest`
 *
 * Both are service-JWT only (`X-Ordo-Internal-Token`, audience = the app's
 * slug), MUST be issued by HQ, and the acting user carried in the JWT must be
 * an HQ admin — the same person who could archive the user in HQ. Apps check
 * that with `verifyOpenWorkCaller`, and check the reassignment target with
 * `resolveReassignTarget`, so the rule is identical in every app.
 *
 * This module is the wire contract: HQ's client parses these shapes, and every
 * app answers with them. Change it additively only — apps and HQ deploy on
 * their own clocks.
 */
import { type OrdoAppSlug, OrdoAppSlugs } from '@ordo/constants/apps'

import { hasAppAccess, hasAppRole } from './permissions'
import { type ServiceSessionClaims, ServiceSessionVerifyError, verifyServiceSessionJWT } from './service-jwt'
import type { PublicUser } from './types'

/** Path prefix every app mounts the two open-work endpoints under. */
export const OPEN_WORK_BASE_PATH = '/api/service/open-work'

/** The only app allowed to call the open-work endpoints. */
export const OPEN_WORK_CALLER: OrdoAppSlug = OrdoAppSlugs.HQ

/** Items listed per category in a preview (the count is always the full total). */
export const OPEN_WORK_PREVIEW_ITEM_LIMIT = 5

export function openWorkPreviewPath(multipassUserId: string): string {
  return `${OPEN_WORK_BASE_PATH}/${encodeURIComponent(multipassUserId)}`
}

export function openWorkReassignPath(multipassUserId: string): string {
  return `${openWorkPreviewPath(multipassUserId)}/reassign`
}

/** One piece of open work, for the short list a preview shows. */
export interface OpenWorkItem {
  id: string
  title: string
  /** A short second line ("Proposed · Oct 21", "Due Oct 30"…). */
  detail?: string | null
  /** Absolute link to the item in its app, when it has a page. */
  url?: string | null
}

/** One kind of open work in an app ("Open tasks", "Event assignments"…). */
export interface OpenWorkCategory {
  /** Stable machine key, unique within the app (`tasks`, `assignments`…). */
  kind: string
  label: string
  count: number
  /** At most `OPEN_WORK_PREVIEW_ITEM_LIMIT`, soonest/most relevant first. */
  items: OpenWorkItem[]
}

export interface OpenWorkPreview {
  /** Multipass slug of the answering app. */
  app: string
  multipassUserId: string
  /** False when the person has no local user in the app at all (so nothing to move). */
  hasLocalUser: boolean
  total: number
  categories: OpenWorkCategory[]
}

export interface OpenWorkReassignRequest {
  toMultipassUserId: string
}

export interface OpenWorkMoved {
  kind: string
  label: string
  count: number
}

/** Open work the app found but deliberately did not move, and why. */
export interface OpenWorkSkipped {
  kind: string
  id: string
  title: string
  reason: string
}

export interface OpenWorkReassignResult {
  app: string
  fromMultipassUserId: string
  toMultipassUserId: string
  /** Sum of `moved[].count`. */
  total: number
  moved: OpenWorkMoved[]
  skipped: OpenWorkSkipped[]
}

/** Why a reassignment target was refused — `error` carries the sentence to show. */
export type ReassignRefusalReason =
  | 'same_person'
  | 'target_not_found'
  | 'target_inactive'
  | 'target_not_staff'
  | 'target_no_access'

/** Body of a 422 refusal from the reassign endpoint. */
export interface OpenWorkRefusal {
  error: string
  reason: ReassignRefusalReason
}

/** Sum the per-category counts into a preview/result total. */
export function sumOpenWork(rows: readonly { count: number }[]): number {
  return rows.reduce((total, row) => total + row.count, 0)
}

export type OpenWorkCallerCheck =
  | { ok: true; claims: ServiceSessionClaims }
  | { ok: false; status: 401 | 403 | 503; error: string }

export interface VerifyOpenWorkCallerOptions {
  /** Shared `MULTIPASS_TOKEN_SECRET`. Missing = the endpoint answers 503. */
  secret: string | null | undefined
  /** The receiving app's own slug — the JWT's expected audience. */
  appSlug: OrdoAppSlug
}

/**
 * Authorize a call to an open-work endpoint from the raw
 * `X-Ordo-Internal-Token` header value. Requires a valid service JWT for this
 * app, issued by HQ, whose acting user is an HQ admin. A cookie session is
 * never enough — these endpoints move other people's work.
 */
export async function verifyOpenWorkCaller(
  token: string | null | undefined,
  opts: VerifyOpenWorkCallerOptions,
): Promise<OpenWorkCallerCheck> {
  if (!opts.secret) {
    return { ok: false, status: 503, error: 'Service auth is not configured' }
  }
  if (!token) {
    return { ok: false, status: 401, error: 'Service token required' }
  }
  let claims: ServiceSessionClaims
  try {
    claims = await verifyServiceSessionJWT(token, { secret: opts.secret, expectedAudience: opts.appSlug })
  } catch (err) {
    if (err instanceof ServiceSessionVerifyError) {
      return { ok: false, status: 401, error: 'Invalid service token' }
    }
    throw err
  }
  if (claims.iss !== OPEN_WORK_CALLER) {
    return { ok: false, status: 403, error: 'Only HQ can read or move open work' }
  }
  if (!claims.user.active || !hasAppRole(claims.user, OrdoAppSlugs.HQ, 'ADMIN')) {
    return { ok: false, status: 403, error: 'HQ admin required' }
  }
  return { ok: true, claims }
}

/** Structural slice of `MultipassClient` — `getUserForService` only. */
export interface ReassignTargetSource {
  getUserForService(id: string): Promise<PublicUser | null>
}

export interface ResolveReassignTargetInput {
  source: ReassignTargetSource
  fromMultipassUserId: string
  toMultipassUserId: string
  /** The receiving app's slug — the target must have access to it. */
  appSlug: string
  /** Display name used in the refusal sentence ("Schedule"). */
  appName: string
}

export type ResolveReassignTargetResult = { ok: true; user: PublicUser } | ({ ok: false } & OpenWorkRefusal)

/**
 * The one rule for who may receive someone's open work: an active INTERNAL
 * (staff) Multipass user, other than the person being cleared, with access to
 * the app. Reads the target fresh from Multipass so a just-archived or
 * just-removed person is refused.
 */
export async function resolveReassignTarget(input: ResolveReassignTargetInput): Promise<ResolveReassignTargetResult> {
  if (input.toMultipassUserId === input.fromMultipassUserId) {
    return { ok: false, reason: 'same_person', error: 'Pick someone other than the person whose work is moving.' }
  }
  const user = await input.source.getUserForService(input.toMultipassUserId)
  if (!user) {
    return { ok: false, reason: 'target_not_found', error: 'That person does not exist in Multipass.' }
  }
  const who = user.name ?? user.email
  if (!user.active) {
    return { ok: false, reason: 'target_inactive', error: `${who} is not active.` }
  }
  if (user.userType !== 'INTERNAL') {
    return { ok: false, reason: 'target_not_staff', error: `${who} is not Ordo staff.` }
  }
  if (!hasAppAccess(user, input.appSlug)) {
    return { ok: false, reason: 'target_no_access', error: `${who} has no access to ${input.appName}.` }
  }
  return { ok: true, user }
}
